import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../hooks/useApi';
import { useUserContext } from '../context/UserContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface EntitlementSummary {
  marketId: number | null;
  marketName: string | null;
  neighborhoodId: number | null;
  neighborhoodName: string | null;
}

interface AdminUser {
  id: number;
  email: string;
  displayName: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  isOrgManager: boolean;
  organizationId: number | null;
  organizationName: string | null;
  createdAt: string;
  entitlements: EntitlementSummary[];
}

interface AdminOrg {
  id: number;
  name: string;
  logoUrl: string | null;
  userEmailDomain: string | null;
  createdAt: string;
  userCount: number;
  isApproved: boolean;
}

interface AdminNeighborhood {
  id: number;
  name: string;
  inMarket: boolean;
}

interface AdminMarket {
  id: number;
  name: string;
  isActive: boolean;
  photoUrl: string | null;
  neighborhoods: AdminNeighborhood[];
}

interface AdminChart {
  id: number;
  name: string;
  title: string;
  description: string | null;
  storedProcedure: string;
  isActive: boolean;
  isBuilding: boolean;
  marketIds: number[];
}

interface AdminNeighborhoodDetail {
  id: number;
  name: string;
  photoUrl: string | null;
  inMarket: boolean;
}

interface AdminBuilding {
  id: number;
  name: string;
  address: string | null;
  photoUrl: string | null;
}

interface AdminEtlRun {
  id: number;
  sourceName: string;
  entityType: string;
  startedAt: string;
  completedAt: string | null;
  recordsProcessed: number;
  recordsInserted: number;
  recordsUpdated: number;
  recordsSkipped: number;
  status: string;
  errorMessage: string | null;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const th: React.CSSProperties = {
  background: '#DCE4E5', padding: '8px 12px', textAlign: 'left',
  fontFamily: "'Montserrat', sans-serif", fontSize: 13, fontWeight: 600,
  borderBottom: '1px solid #ccc',
};
const td: React.CSSProperties = {
  padding: '8px 12px', fontFamily: "'Montserrat', sans-serif", fontSize: 13,
  borderBottom: '1px solid #eee', verticalAlign: 'middle',
};
const btn = (variant: 'primary' | 'secondary' | 'danger' = 'secondary'): React.CSSProperties => ({
  fontFamily: "'Montserrat', sans-serif", fontSize: 13, cursor: 'pointer',
  padding: '5px 14px', borderRadius: 4,
  background: variant === 'primary' ? '#958945' : variant === 'danger' ? '#d60a22' : '#fff',
  color: variant === 'primary' || variant === 'danger' ? '#fff' : '#333',
  border: variant === 'secondary' ? '1px solid #ccc' : 'none',
});
const input: React.CSSProperties = {
  fontFamily: "'Montserrat', sans-serif", fontSize: 13, padding: '5px 9px',
  border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box',
};
const sectionHead: React.CSSProperties = {
  fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 14,
  color: '#333', marginBottom: 8, marginTop: 16,
};
const label: React.CSSProperties = {
  fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#333',
  display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
};

// ── UsersTab ──────────────────────────────────────────────────────────────────

function UsersTab({ authFetch, markets }: { authFetch: Function; markets: AdminMarket[] }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editAdmin, setEditAdmin] = useState(false);
  const [editSuspended, setEditSuspended] = useState(false);
  const [editOrgManager, setEditOrgManager] = useState(false);
  const [editOrgId, setEditOrgId] = useState<number | ''>('');
  const [editMarketIds, setEditMarketIds] = useState<Set<number>>(new Set());
  const [editNeighborhoodIds, setEditNeighborhoodIds] = useState<Set<number>>(new Set());
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    authFetch('/api/admin/users').then((r: Response) => r.json()).then(setUsers);
    authFetch('/api/admin/organizations').then((r: Response) => r.json()).then(setOrgs);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (u: AdminUser) => {
    setSelected(u);
    setEditName(u.displayName ?? '');
    setEditAdmin(u.isAdmin);
    setEditSuspended(u.isSuspended);
    setEditOrgManager(u.isOrgManager);
    setEditOrgId(u.organizationId ?? '');
    setEditMarketIds(new Set(u.entitlements.filter(e => e.marketId).map(e => e.marketId!)));
    setEditNeighborhoodIds(new Set(u.entitlements.filter(e => e.neighborhoodId).map(e => e.neighborhoodId!)));
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    await authFetch(`/api/admin/users/${selected.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        displayName: editName || null,
        isAdmin: editAdmin,
        isSuspended: editSuspended,
        isOrgManager: editOrgManager,
        organizationId: editOrgId === '' ? null : Number(editOrgId),
      }),
    });
    await authFetch(`/api/admin/users/${selected.id}/entitlements`, {
      method: 'PUT',
      body: JSON.stringify({
        marketIds: Array.from(editMarketIds),
        neighborhoodIds: Array.from(editNeighborhoodIds),
      }),
    });
    setSaving(false);
    setSelected(null);
    load();
  };

  const toggleMarket = (id: number) => setEditMarketIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleNeighborhood = (id: number) => setEditNeighborhoodIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.displayName ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <input style={{ ...input, width: 320, marginBottom: 16 }} placeholder="Search by email or name…"
        value={search} onChange={e => setSearch(e.target.value)} />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Email</th>
            <th style={th}>Name</th>
            <th style={th}>Admin</th>
            <th style={th}>Suspended</th>
            <th style={th}>Org Manager</th>
            <th style={th}>Organization</th>
            <th style={th}>Entitlements</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((u, i) => (
            <React.Fragment key={u.id}>
              <tr
                style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA', cursor: 'pointer' }}
                onClick={() => selected?.id === u.id ? setSelected(null) : openEdit(u)}
              >
                <td style={td}>{u.email}</td>
                <td style={td}>{u.displayName ?? <span style={{ color: '#aaa' }}>—</span>}</td>
                <td style={td}>{u.isAdmin ? '✓' : ''}</td>
                <td style={td}>{u.isSuspended ? '✓' : ''}</td>
                <td style={td}>{u.isOrgManager ? '✓' : ''}</td>
                <td style={td}>{u.organizationName ?? <span style={{ color: '#aaa' }}>—</span>}</td>
                <td style={td}>{u.entitlements.length > 0
                  ? u.entitlements.map(e => e.marketName ?? e.neighborhoodName).join(', ')
                  : <span style={{ color: '#aaa' }}>None</span>}
                </td>
              </tr>
              {selected?.id === u.id && (
                <tr>
                  <td colSpan={7} style={{ padding: 20, background: '#f9f8f6', borderBottom: '1px solid #ccc' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      <div>
                        <div style={sectionHead}>Profile</div>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ ...label, marginBottom: 4 }}>Display Name</div>
                          <input style={input} value={editName}
                            onChange={e => setEditName(e.target.value)} placeholder="Display name" />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ ...label, marginBottom: 4 }}>Organization</div>
                          <select style={{ ...input, background: '#fff' }} value={editOrgId}
                            onChange={e => setEditOrgId(e.target.value === '' ? '' : Number(e.target.value))}>
                            <option value="">— None —</option>
                            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                          <label style={label}>
                            <input type="checkbox" checked={editAdmin} onChange={e => setEditAdmin(e.target.checked)} />
                            Admin
                          </label>
                          <label style={label}>
                            <input type="checkbox" checked={editSuspended} onChange={e => setEditSuspended(e.target.checked)} />
                            Suspended
                          </label>
                          <label style={label}>
                            <input type="checkbox" checked={editOrgManager} onChange={e => setEditOrgManager(e.target.checked)} />
                            Org Manager
                          </label>
                        </div>
                      </div>
                      <div>
                        <div style={sectionHead}>Market Entitlements</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: 12 }}>
                          {markets.map(m => (
                            <label key={m.id} style={label}>
                              <input type="checkbox" checked={editMarketIds.has(m.id)}
                                onChange={() => toggleMarket(m.id)} />
                              {m.name}
                            </label>
                          ))}
                        </div>
                        <div style={sectionHead}>Neighborhood Entitlements</div>
                        <div style={{ maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
                          {markets.map(m => (
                            <div key={m.id} style={{ marginBottom: 8 }}>
                              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 12, color: '#66635F', fontWeight: 600, marginBottom: 4 }}>{m.name}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', paddingLeft: 8 }}>
                                {m.neighborhoods.map(n => (
                                  <label key={n.id} style={label}>
                                    <input type="checkbox" checked={editNeighborhoodIds.has(n.id)}
                                      onChange={() => toggleNeighborhood(n.id)} />
                                    {n.name}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                      <button style={btn('primary')} onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      <button style={btn()} onClick={() => setSelected(null)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── OrgsTab ───────────────────────────────────────────────────────────────────

function OrgsTab({ authFetch }: { authFetch: Function }) {
  const { getAccessTokenSilently } = useAuth0();
  const [orgs, setOrgs] = useState<AdminOrg[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [editApproved, setEditApproved] = useState(true);
  const [saving, setSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoTargetId, setLogoTargetId] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    authFetch('/api/admin/organizations')
      .then((r: Response) => {
        if (!r.ok) throw new Error(`API error ${r.status}`);
        return r.json();
      })
      .then(setOrgs)
      .catch((e: Error) => setLoadError(e.message));
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    await authFetch('/api/admin/organizations', {
      method: 'POST',
      body: JSON.stringify({ name: newName.trim(), userEmailDomain: newDomain.trim() || null }),
    });
    setNewName(''); setNewDomain('');
    setCreating(false);
    load();
  };

  const openEdit = (o: AdminOrg) => {
    setEditId(o.id); setEditName(o.name); setEditDomain(o.userEmailDomain ?? ''); setEditApproved(o.isApproved);
  };

  const saveEdit = async () => {
    if (!editId) return;
    setSaving(true);
    await authFetch(`/api/admin/organizations/${editId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: editName.trim(), userEmailDomain: editDomain.trim() || null, isApproved: editApproved }),
    });
    setSaving(false);
    setEditId(null);
    load();
  };

  const uploadLogo = async (file: File, orgId: number) => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const form = new FormData();
    form.append('file', file);
    await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/admin/organizations/${orgId}/logo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    load();
  };

  return (
    <div>
      <div style={{ background: '#f9f8f6', border: '1px solid #e0ddd8', borderRadius: 6, padding: 16, marginBottom: 24 }}>
        <div style={sectionHead}>New Organization</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ ...label, marginBottom: 4 }}>Name *</div>
            <input style={input} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Org name" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ ...label, marginBottom: 4 }}>Email Domains</div>
            <input style={input} value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="example.com, other.com" />
          </div>
          <button style={btn('primary')} onClick={create} disabled={creating || !newName.trim()}>
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>

      {loadError && (
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: 'rgb(214,10,34)', marginBottom: 12 }}>
          Failed to load organizations: {loadError}
        </div>
      )}

      <input
        ref={logoInputRef} type="file" accept=".jpg,.jpeg,.png,.svg"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file && logoTargetId) uploadLogo(file, logoTargetId);
          e.target.value = '';
        }}
      />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Name</th>
            <th style={th}>Email Domains</th>
            <th style={th}>Users</th>
            <th style={th}>Status</th>
            <th style={th}>Logo</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o, i) => (
            <React.Fragment key={o.id}>
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                <td style={td}>{o.name || <span style={{ color: '#aaa' }}>(unnamed)</span>}</td>
                <td style={td}>{o.userEmailDomain ?? <span style={{ color: '#aaa' }}>—</span>}</td>
                <td style={td}>{o.userCount}</td>
                <td style={td}>
                  {o.isApproved
                    ? <span style={{ color: '#037B66' }}>Approved</span>
                    : <span style={{ fontWeight: 600, color: '#ba9394' }}>Pending Approval</span>}
                </td>
                <td style={td}>
                  {o.logoUrl
                    ? <img src={o.logoUrl} alt="" style={{ height: 28, maxWidth: 80, objectFit: 'contain' }} />
                    : <span style={{ color: '#aaa' }}>—</span>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button style={btn()} onClick={() => editId === o.id ? setEditId(null) : openEdit(o)}>Edit</button>
                    <button style={btn()} onClick={() => { setLogoTargetId(o.id); logoInputRef.current?.click(); }}>
                      Upload Logo
                    </button>
                  </div>
                </td>
              </tr>
              {editId === o.id && (
                <tr>
                  <td colSpan={6} style={{ padding: 16, background: '#f9f8f6', borderBottom: '1px solid #ccc' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ ...label, marginBottom: 4 }}>Name *</div>
                        <input style={input} value={editName} onChange={e => setEditName(e.target.value)} />
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ ...label, marginBottom: 4 }}>Email Domains</div>
                        <input style={input} value={editDomain} onChange={e => setEditDomain(e.target.value)} placeholder="example.com, other.com" />
                      </div>
                      <label style={label}>
                        <input type="checkbox" checked={editApproved} onChange={e => setEditApproved(e.target.checked)} />
                        Approved
                      </label>
                      <button style={btn('primary')} onClick={saveEdit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                      <button style={btn()} onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── MarketsTab ────────────────────────────────────────────────────────────────

function MarketsTab({ authFetch, markets, onMarketsChange }: {
  authFetch: Function;
  markets: AdminMarket[];
  onMarketsChange: () => void;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [expandedMarketId, setExpandedMarketId] = useState<number | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoTargetId, setPhotoTargetId] = useState<number | null>(null);

  const uploadPhoto = async (file: File, marketId: number) => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const form = new FormData();
    form.append('file', file);
    await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/admin/markets/${marketId}/photo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    onMarketsChange();
  };

  const deletePhoto = async (marketId: number) => {
    setDeletingPhoto(marketId);
    await authFetch(`/api/admin/markets/${marketId}/photo`, { method: 'DELETE' });
    setDeletingPhoto(null);
    onMarketsChange();
  };

  const toggleIsActive = async (market: AdminMarket) => {
    setToggling(`market-${market.id}`);
    await authFetch(`/api/admin/markets/${market.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: !market.isActive }),
    });
    onMarketsChange();
    setToggling(null);
  };

  const toggleNeighborhood = async (marketId: number, neighborhoodId: number, inMarket: boolean) => {
    const key = `nh-${marketId}-${neighborhoodId}`;
    setToggling(key);
    if (inMarket) {
      await authFetch(`/api/admin/markets/${marketId}/neighborhoods/${neighborhoodId}`, { method: 'DELETE' });
    } else {
      await authFetch(`/api/admin/markets/${marketId}/neighborhoods/${neighborhoodId}`, { method: 'POST' });
    }
    onMarketsChange();
    setToggling(null);
  };

  return (
    <div>
      <input
        ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file && photoTargetId) uploadPhoto(file, photoTargetId);
          e.target.value = '';
        }}
      />
      {markets.map((m, i) => (
        <div key={m.id} style={{
          border: '1px solid #e0ddd8', borderRadius: 6, marginBottom: 12,
          background: i % 2 === 0 ? '#fff' : '#faf9f7',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', cursor: 'pointer',
          }}
            onClick={() => setExpandedMarketId(expandedMarketId === m.id ? null : m.id)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {m.photoUrl
                ? <a href={m.photoUrl} target="_blank" rel="noreferrer"><img src={m.photoUrl} alt="" style={{ height: 36, width: 56, objectFit: 'cover', borderRadius: 3, display: 'block' }} /></a>
                : <div style={{ height: 36, width: 56, background: '#e0ddd8', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 10, color: '#aaa' }}>No photo</span>
                  </div>}
              <span style={{
                fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: 15, color: '#333'
              }}>{m.name}</span>
              <span style={{
                fontFamily: "'Montserrat', sans-serif", fontSize: 12, color: '#66635F',
              }}>
                {m.neighborhoods.filter(n => n.inMarket).length} of {m.neighborhoods.length} neighborhoods assigned
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ ...label, gap: 6 }} onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox" checked={m.isActive}
                  disabled={toggling === `market-${m.id}`}
                  onChange={() => toggleIsActive(m)}
                />
                <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>Active</span>
              </label>
              <span style={{ color: '#958945', fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
                {expandedMarketId === m.id ? '▲' : '▼'}
              </span>
            </div>
          </div>

          {expandedMarketId === m.id && (
            <div style={{ borderTop: '1px solid #e0ddd8', padding: '12px 16px' }}>
              <div style={{ ...sectionHead, marginTop: 0 }}>Hero Photo</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                {m.photoUrl && (
                  <a href={m.photoUrl} target="_blank" rel="noreferrer"><img src={m.photoUrl} alt="" style={{ height: 60, width: 96, objectFit: 'cover', borderRadius: 4, display: 'block' }} /></a>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={btn()} onClick={e => { e.stopPropagation(); setPhotoTargetId(m.id); photoInputRef.current?.click(); }}>
                    {m.photoUrl ? 'Replace Photo' : 'Upload Photo'}
                  </button>
                  {m.photoUrl && (
                    <button style={btn('danger')} disabled={deletingPhoto === m.id}
                      onClick={e => { e.stopPropagation(); deletePhoto(m.id); }}>
                      {deletingPhoto === m.id ? '…' : 'Delete Photo'}
                    </button>
                  )}
                </div>
              </div>
              <div style={{ ...sectionHead, marginTop: 0 }}>Neighborhood Assignments</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '6px 12px' }}>
                {m.neighborhoods.map(n => {
                  const key = `nh-${m.id}-${n.id}`;
                  return (
                    <label key={n.id} style={{ ...label, opacity: toggling === key ? 0.5 : 1 }}>
                      <input
                        type="checkbox" checked={n.inMarket}
                        disabled={toggling === key}
                        onChange={() => toggleNeighborhood(m.id, n.id, n.inMarket)}
                      />
                      {n.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── ChartsTab ─────────────────────────────────────────────────────────────────

function marketSummary(chart: AdminChart, markets: AdminMarket[]): string {
  const names = markets.filter(m => chart.marketIds.includes(m.id)).map(m => m.name);
  if (names.length === 0) return 'None';
  if (names.length <= 2) return names.join(', ');
  return `${names[0]}, ${names[1]}, and ${names.length - 2} more`;
}

function ChartsTab({ authFetch, markets }: { authFetch: Function; markets: AdminMarket[] }) {
  const [charts, setCharts] = useState<AdminChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    authFetch('/api/admin/charts')
      .then((r: Response) => r.json())
      .then((data: AdminChart[]) => { setCharts(data); setLoading(false); });
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const toggleFlag = async (chart: AdminChart, field: 'isActive' | 'isBuilding') => {
    const key = `${field}-${chart.id}`;
    setToggling(key);
    await authFetch(`/api/admin/charts/${chart.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        title: chart.title,
        description: chart.description,
        isActive: field === 'isActive' ? !chart.isActive : chart.isActive,
        isBuilding: field === 'isBuilding' ? !chart.isBuilding : chart.isBuilding,
      }),
    });
    setToggling(null);
    load();
  };

  const startEdit = (chart: AdminChart) => {
    setEditId(chart.id);
    setEditTitle(chart.title);
    setEditDescription(chart.description ?? '');
  };

  const saveEdit = async (chart: AdminChart) => {
    setSaving(true);
    await authFetch(`/api/admin/charts/${chart.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        title: editTitle,
        description: editDescription,
        isActive: chart.isActive,
        isBuilding: chart.isBuilding,
      }),
    });
    setSaving(false);
    setEditId(null);
    load();
  };

  const toggleMarket = async (chart: AdminChart, marketId: number) => {
    const key = `mkt-${chart.id}-${marketId}`;
    setToggling(key);
    const assigned = chart.marketIds.includes(marketId);
    if (assigned) {
      await authFetch(`/api/admin/charts/${chart.id}/markets/${marketId}`, { method: 'DELETE' });
    } else {
      await authFetch(`/api/admin/charts/${chart.id}/markets/${marketId}`, { method: 'POST' });
    }
    setToggling(null);
    load();
  };

  if (loading) return <div style={{ fontFamily: "'Montserrat', sans-serif", color: '#66635F', padding: 24 }}>Loading…</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
        <thead>
          <tr>
            <th style={th}>Title</th>
            <th style={th}>Description</th>
            <th style={th}>Internal Name</th>
            <th style={th}>Stored Procedure</th>
            <th style={th}>Active</th>
            <th style={th}>Building-level</th>
            <th style={th}>Markets</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {charts.map((c, i) => (
            <React.Fragment key={c.id}>
              <tr style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                <td style={td}>{c.title}</td>
                <td style={{ ...td, color: '#66635F', fontSize: 12, maxWidth: 260 }}>{c.description}</td>
                <td style={{ ...td, color: '#66635F', fontSize: 12 }}>{c.name}</td>
                <td style={{ ...td, color: '#66635F', fontSize: 12 }}>{c.storedProcedure}</td>
                <td style={td}>
                  <input
                    type="checkbox" checked={c.isActive}
                    disabled={toggling === `isActive-${c.id}`}
                    onChange={() => toggleFlag(c, 'isActive')}
                  />
                </td>
                <td style={td}>
                  <input
                    type="checkbox" checked={c.isBuilding}
                    disabled={toggling === `isBuilding-${c.id}`}
                    onChange={() => toggleFlag(c, 'isBuilding')}
                  />
                </td>
                <td style={{ ...td, color: '#66635F', fontSize: 12 }}>{marketSummary(c, markets)}</td>
                <td style={td}>
                  <button style={btn()} onClick={() => startEdit(c)}>Edit</button>
                </td>
              </tr>
              {editId === c.id && (
                <tr>
                  <td colSpan={7} style={{ padding: '12px 16px', background: '#f9f8f6', borderBottom: '1px solid #ccc' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={label}>Title</label>
                        <input
                          style={input}
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') setEditId(null); }}
                          autoFocus
                        />
                      </div>
                      <div>
                        <label style={label}>Description</label>
                        <textarea
                          style={{ ...input, minHeight: 80, resize: 'vertical' }}
                          value={editDescription}
                          onChange={e => setEditDescription(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Escape') setEditId(null); }}
                        />
                      </div>
                      <div>
                        <label style={label}>Markets</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                          {markets.map(m => (
                            <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'Montserrat', sans-serif", fontSize: 13 }}>
                              <input
                                type="checkbox" checked={c.marketIds.includes(m.id)}
                                disabled={toggling === `mkt-${c.id}-${m.id}`}
                                onChange={() => toggleMarket(c, m.id)}
                              />
                              {m.name}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button style={btn('primary')} onClick={() => saveEdit(c)} disabled={saving || !editTitle.trim()}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button style={btn()} onClick={() => setEditId(null)}>Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── NeighborhoodsTab ──────────────────────────────────────────────────────────

function NeighborhoodsTab({ authFetch, markets }: { authFetch: Function; markets: AdminMarket[] }) {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedMarketId, setSelectedMarketId] = useState<number | ''>('');
  const [neighborhoods, setNeighborhoods] = useState<AdminNeighborhoodDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoTargetId, setPhotoTargetId] = useState<number | null>(null);

  const load = useCallback((marketId: number) => {
    setLoading(true);
    authFetch(`/api/admin/neighborhoods?marketId=${marketId}`)
      .then((r: Response) => r.json())
      .then((data: AdminNeighborhoodDetail[]) => {
        setNeighborhoods(data);
        setLoading(false);
      });
  }, [authFetch]);

  const onMarketChange = (id: number | '') => {
    setSelectedMarketId(id);
    setEditId(null);
    setNeighborhoods([]);
    if (id !== '') load(id as number);
  };

  const saveName = async () => {
    if (!editId) return;
    setSaving(true);
    await authFetch(`/api/admin/neighborhoods/${editId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: editName }),
    });
    setSaving(false);
    setEditId(null);
    if (selectedMarketId !== '') load(selectedMarketId as number);
  };

  const uploadPhoto = async (file: File, neighborhoodId: number) => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const form = new FormData();
    form.append('file', file);
    await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/admin/neighborhoods/${neighborhoodId}/photo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (selectedMarketId !== '') load(selectedMarketId as number);
  };

  const deletePhoto = async (neighborhoodId: number) => {
    setDeleting(neighborhoodId);
    await authFetch(`/api/admin/neighborhoods/${neighborhoodId}/photo`, { method: 'DELETE' });
    setDeleting(null);
    if (selectedMarketId !== '') load(selectedMarketId as number);
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <select
          style={{ ...input, width: 280, background: '#fff' }}
          value={selectedMarketId}
          onChange={e => onMarketChange(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">— Select a market —</option>
          {markets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      {loading && (
        <div style={{ fontFamily: "'Montserrat', sans-serif", color: '#66635F', padding: 24 }}>Loading…</div>
      )}

      {!loading && selectedMarketId !== '' && (
        <>
          <input
            ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file && photoTargetId) uploadPhoto(file, photoTargetId);
              e.target.value = '';
            }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>In Market</th>
                <th style={th}>Photo</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {neighborhoods.map((n, i) => (
                <React.Fragment key={n.id}>
                  <tr style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                    <td style={td}>{n.name}</td>
                    <td style={td}>
                      <span style={{ color: n.inMarket ? '#1CA11C' : '#aaa', fontWeight: 600 }}>
                        {n.inMarket ? '✓' : '—'}
                      </span>
                    </td>
                    <td style={td}>
                      {n.photoUrl
                        ? <a href={n.photoUrl} target="_blank" rel="noreferrer"><img src={n.photoUrl} alt="" style={{ height: 40, width: 64, objectFit: 'cover', borderRadius: 3, display: 'block' }} /></a>
                        : <span style={{ color: '#aaa' }}>—</span>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button style={btn()} onClick={() => { setEditId(n.id); setEditName(n.name); }}>
                          Edit Name
                        </button>
                        <button style={btn()} onClick={() => { setPhotoTargetId(n.id); photoInputRef.current?.click(); }}>
                          {n.photoUrl ? 'Replace Photo' : 'Upload Photo'}
                        </button>
                        {n.photoUrl && (
                          <button style={btn('danger')} disabled={deleting === n.id}
                            onClick={() => deletePhoto(n.id)}>
                            {deleting === n.id ? '…' : 'Delete Photo'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editId === n.id && (
                    <tr>
                      <td colSpan={4} style={{ padding: '12px 16px', background: '#f9f8f6', borderBottom: '1px solid #ccc' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <input
                            style={{ ...input, width: 300 }}
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditId(null); }}
                            autoFocus
                          />
                          <button style={btn('primary')} onClick={saveName} disabled={saving || !editName.trim()}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button style={btn()} onClick={() => setEditId(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {neighborhoods.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...td, color: '#aaa', textAlign: 'center', padding: 32 }}>
                    No neighborhoods found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── BuildingsTab ──────────────────────────────────────────────────────────────

function BuildingsTab({ authFetch, markets }: { authFetch: Function; markets: AdminMarket[] }) {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedMarketId, setSelectedMarketId] = useState<number | ''>('');
  const [neighborhoods, setNeighborhoods] = useState<AdminNeighborhoodDetail[]>([]);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<number | ''>('');
  const [buildings, setBuildings] = useState<AdminBuilding[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoTargetId, setPhotoTargetId] = useState<number | null>(null);

  const loadBuildings = useCallback((neighborhoodId: number) => {
    setLoadingBuildings(true);
    authFetch(`/api/admin/buildings?neighborhoodId=${neighborhoodId}`)
      .then((r: Response) => r.json())
      .then((data: AdminBuilding[]) => {
        setBuildings(data);
        setLoadingBuildings(false);
      });
  }, [authFetch]);

  const onMarketChange = (id: number | '') => {
    setSelectedMarketId(id);
    setSelectedNeighborhoodId('');
    setNeighborhoods([]);
    setBuildings([]);
    if (id === '') return;
    setLoadingNeighborhoods(true);
    authFetch(`/api/admin/neighborhoods?marketId=${id}`)
      .then((r: Response) => r.json())
      .then((data: AdminNeighborhoodDetail[]) => {
        setNeighborhoods(data);
        setLoadingNeighborhoods(false);
      });
  };

  const onNeighborhoodChange = (id: number | '') => {
    setSelectedNeighborhoodId(id);
    setBuildings([]);
    if (id !== '') loadBuildings(id as number);
  };

  const uploadPhoto = async (file: File, buildingId: number) => {
    const token = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
    });
    const form = new FormData();
    form.append('file', file);
    await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/admin/buildings/${buildingId}/photo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (selectedNeighborhoodId !== '') loadBuildings(selectedNeighborhoodId as number);
  };

  const deletePhoto = async (buildingId: number) => {
    setDeleting(buildingId);
    await authFetch(`/api/admin/buildings/${buildingId}/photo`, { method: 'DELETE' });
    setDeleting(null);
    if (selectedNeighborhoodId !== '') loadBuildings(selectedNeighborhoodId as number);
  };

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
        <select
          style={{ ...input, width: 280, background: '#fff' }}
          value={selectedMarketId}
          onChange={e => onMarketChange(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">— Select a market —</option>
          {markets.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select
          style={{ ...input, width: 280, background: '#fff' }}
          value={selectedNeighborhoodId}
          disabled={selectedMarketId === '' || loadingNeighborhoods}
          onChange={e => onNeighborhoodChange(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">
            {loadingNeighborhoods ? 'Loading…' : '— Select a neighborhood —'}
          </option>
          {neighborhoods.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
        </select>
      </div>

      {loadingBuildings && (
        <div style={{ fontFamily: "'Montserrat', sans-serif", color: '#66635F', padding: 24 }}>Loading…</div>
      )}

      {!loadingBuildings && selectedNeighborhoodId !== '' && (
        <>
          <input
            ref={photoInputRef} type="file" accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file && photoTargetId) uploadPhoto(file, photoTargetId);
              e.target.value = '';
            }}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Address</th>
                <th style={th}>Photo</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {buildings.map((b, i) => (
                <tr key={b.id} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                  <td style={td}>{b.name}</td>
                  <td style={td}>{b.address ?? <span style={{ color: '#aaa' }}>—</span>}</td>
                  <td style={td}>
                    {b.photoUrl
                      ? <a href={b.photoUrl} target="_blank" rel="noreferrer"><img src={b.photoUrl} alt="" style={{ height: 40, width: 64, objectFit: 'cover', borderRadius: 3, display: 'block' }} /></a>
                      : <span style={{ color: '#aaa' }}>—</span>}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={btn()} onClick={() => { setPhotoTargetId(b.id); photoInputRef.current?.click(); }}>
                        {b.photoUrl ? 'Replace Photo' : 'Upload Photo'}
                      </button>
                      {b.photoUrl && (
                        <button style={btn('danger')} disabled={deleting === b.id}
                          onClick={() => deletePhoto(b.id)}>
                          {deleting === b.id ? '…' : 'Delete Photo'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {buildings.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ ...td, color: '#aaa', textAlign: 'center', padding: 32 }}>
                    No buildings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── EtlTab ────────────────────────────────────────────────────────────────────

function EtlTab({ authFetch }: { authFetch: Function }) {
  const [runs, setRuns] = useState<AdminEtlRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/api/admin/etl/runs').then((r: Response) => r.json()).then((data: AdminEtlRun[]) => {
      setRuns(data);
      setLoading(false);
    });
  }, [authFetch]);

  const formatDuration = (run: AdminEtlRun) => {
    if (!run.completedAt) return '—';
    const ms = new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime();
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const statusColor = (status: string) => {
    if (status === 'Success') return '#1CA11C';
    if (status === 'Error') return 'rgb(214,10,34)';
    return '#66635F';
  };

  if (loading) return <div style={{ fontFamily: "'Montserrat', sans-serif", color: '#66635F', padding: 24 }}>Loading…</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
        <thead>
          <tr>
            <th style={th}>Source</th>
            <th style={th}>Entity</th>
            <th style={th}>Started</th>
            <th style={th}>Duration</th>
            <th style={th}>Status</th>
            <th style={th}>Processed</th>
            <th style={th}>Inserted</th>
            <th style={th}>Updated</th>
            <th style={th}>Skipped</th>
            <th style={th}>Error</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r, i) => (
            <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
              <td style={td}>{r.sourceName}</td>
              <td style={td}>{r.entityType}</td>
              <td style={td}>{new Date(r.startedAt).toLocaleString()}</td>
              <td style={td}>{formatDuration(r)}</td>
              <td style={{ ...td, color: statusColor(r.status), fontWeight: 600 }}>{r.status}</td>
              <td style={td}>{r.recordsProcessed}</td>
              <td style={td}>{r.recordsInserted}</td>
              <td style={td}>{r.recordsUpdated}</td>
              <td style={td}>{r.recordsSkipped}</td>
              <td style={{ ...td, color: 'rgb(214,10,34)', fontSize: 12, maxWidth: 300, wordBreak: 'break-word' }}>
                {r.errorMessage ?? ''}
              </td>
            </tr>
          ))}
          {runs.length === 0 && (
            <tr><td colSpan={10} style={{ ...td, color: '#aaa', textAlign: 'center', padding: 32 }}>No ETL runs found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── AdminPage ─────────────────────────────────────────────────────────────────

type AdminTab = 'users' | 'orgs' | 'markets' | 'neighborhoods' | 'buildings' | 'charts' | 'etl';

export function AdminPage() {
  const { me, loading } = useUserContext();
  const { authFetch } = useApi();
  const [tab, setTab] = useState<AdminTab>('users');
  const [markets, setMarkets] = useState<AdminMarket[]>([]);

  const loadMarkets = useCallback(() => {
    authFetch('/api/admin/markets').then((r: Response) => r.json()).then(setMarkets);
  }, [authFetch]);

  useEffect(() => { loadMarkets(); }, [loadMarkets]);

  if (loading) return null;
  if (!me?.isAdmin) return <Navigate to="/" replace />;

  const tabStyle = (t: AdminTab): React.CSSProperties => ({
    fontFamily: "'Montserrat', sans-serif", fontSize: 14, fontWeight: 600,
    padding: '10px 20px', cursor: 'pointer', border: 'none', background: 'none',
    borderBottom: tab === t ? '3px solid #958945' : '3px solid transparent',
    color: tab === t ? '#958945' : '#66635F',
  });

  return (
    <div style={{ padding: '2rem', fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ marginBottom: 8 }}>
        <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500, fontSize: 30, margin: 0 }}>Admin</h1>
      </div>

      <div style={{ borderBottom: '1px solid #e0ddd8', marginBottom: 24, display: 'flex', gap: 0 }}>
        <button style={tabStyle('users')} onClick={() => setTab('users')}>Users</button>
        <button style={tabStyle('orgs')} onClick={() => setTab('orgs')}>Organizations</button>
        <button style={tabStyle('markets')} onClick={() => setTab('markets')}>Markets</button>
        <button style={tabStyle('neighborhoods')} onClick={() => setTab('neighborhoods')}>Neighborhoods</button>
        <button style={tabStyle('buildings')} onClick={() => setTab('buildings')}>Buildings</button>
        <button style={tabStyle('charts')} onClick={() => setTab('charts')}>Charts</button>
        <button style={tabStyle('etl')} onClick={() => setTab('etl')}>ETL Runs</button>
      </div>

      {tab === 'users' && <UsersTab authFetch={authFetch} markets={markets} />}
      {tab === 'orgs' && <OrgsTab authFetch={authFetch} />}
      {tab === 'markets' && <MarketsTab authFetch={authFetch} markets={markets} onMarketsChange={loadMarkets} />}
      {tab === 'neighborhoods' && <NeighborhoodsTab authFetch={authFetch} markets={markets} />}
      {tab === 'buildings' && <BuildingsTab authFetch={authFetch} markets={markets} />}
      {tab === 'charts' && <ChartsTab authFetch={authFetch} markets={markets} />}
      {tab === 'etl' && <EtlTab authFetch={authFetch} />}
    </div>
  );
}
